import {
    formatIPQValue,
    getIPQOptions,
    IPQ_QUESTIONS,
    type IPQField,
} from '@fruitful/investment-management';
import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown, type DropdownChangeEvent } from 'primereact/dropdown';
import {
    BodySmallRegular,
    H6Emphasized,
    Spinner,
    useToastController,
    YStack,
} from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { adminUserAtom } from '../../entities/entityAtoms';
import type { MemberDetails } from '../../entities/entityTypes';
import { useGetUserInvestmentProfileAdmin } from '../hooks/useGetUserInvestmentProfileAdmin';
import { useUpsertUserInvestmentProfileAdmin } from '../hooks/useUpsertUserInvestmentProfileAdmin';

interface MemberIPQCardProps {
    member: MemberDetails;
}

const MemberIPQCard = ({ member }: MemberIPQCardProps) => {
    const toast = useToastController();
    const adminUser = useAtomValue(adminUserAtom);
    const investmentProfileAuth =
        adminUser.authorization.selectedMembership.investmentProfile;
    const canEdit = investmentProfileAuth.canEdit;

    const { data: profile, isLoading } = useGetUserInvestmentProfileAdmin({
        userId: member.id,
    });
    const { mutateAsync: upsertProfile, isPending: isSaving } =
        useUpsertUserInvestmentProfileAdmin();

    const handleChange = async (field: IPQField, value: string | null) => {
        try {
            await upsertProfile({
                body: { [field]: value ?? undefined },
                urlParams: { userId: member.id },
            });
            toast.show('IPQ updated', MutationSuccessToastOptions);
        } catch {
            toast.show('Error updating IPQ', MutationErrorToastOptions());
        }
    };

    const tableRows = IPQ_QUESTIONS.map(({ key, label }) => ({
        key,
        label,
        value: profile?.[key] ?? null,
    }));

    if (isLoading) {
        return (
            <YStack gap={8}>
                <H6Emphasized>
                    {member.firstName} {member.lastName} - IPQ
                </H6Emphasized>
                <Spinner />
            </YStack>
        );
    }

    return (
        <YStack gap={12}>
            <H6Emphasized>
                {member.firstName} {member.lastName} - IPQ
            </H6Emphasized>
            {investmentProfileAuth.canView && !canEdit && (
                <BodySmallRegular color="$gray1000">
                    Only this member&apos;s guide can edit the investment
                    profile.
                </BodySmallRegular>
            )}
            <DataTable
                value={tableRows}
                size="small"
                tableStyle={{ width: '100%', fontSize: 13 }}
                scrollable
            >
                <Column
                    field="label"
                    header="Question"
                    style={{ minWidth: 280, verticalAlign: 'top' }}
                />
                <Column
                    header="Answer"
                    style={{ minWidth: 200, verticalAlign: 'top' }}
                    body={(row: {
                        key: IPQField;
                        label: string;
                        value: string | null;
                    }) => {
                        const { key, value } = row;
                        const options = getIPQOptions(key);

                        if (canEdit) {
                            return (
                                <Dropdown
                                    value={value}
                                    options={options}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Select"
                                    disabled={isSaving}
                                    onChange={(e: DropdownChangeEvent) => {
                                        const v =
                                            e.value === undefined
                                                ? null
                                                : (e.value as string | null);
                                        void handleChange(key, v);
                                    }}
                                />
                            );
                        }
                        return (
                            <BodySmallRegular>
                                {formatIPQValue(key, value)}
                            </BodySmallRegular>
                        );
                    }}
                />
            </DataTable>
        </YStack>
    );
};

interface MemberProfileIPQSectionProps {
    members: MemberDetails[];
}

export const MemberProfileIPQSection = ({
    members,
}: MemberProfileIPQSectionProps) => {
    return (
        <YStack gap={24}>
            {members.map((member) => (
                <MemberIPQCard key={member.id} member={member} />
            ))}
        </YStack>
    );
};
